/**
 * TypeScript mirror of the Pydantic models on the backend
 * (lambda-layers/control_plane_layer/python/models.py).
 *
 * The backend serializes to camelCase via convert_keys_to_camel(), so every
 * key here is the camelCase form — even though the Python field names are
 * snake_case.
 */

// Gateway type names are catalog-driven (CATALOG#gateways), so any string is
// legal at the type level — runtime is filtered by what the catalog returns.
export type GatewayType = string;
export type TenantOption = "GrandCentral" | "MOMA" | "Flatiron";
export type TenantStatus = "draft" | "active" | "suspended" | "archived";
export type DeploymentStatus =
  | "triggered"
  | "in_progress"
  | "success"
  | "partial_failure"
  | "failed"
  | "unknown";
export type DeploymentKind = "deploy" | "destroy";

// Status of the per-tenant Cognito custom domain provisioning. ``none`` at
// create time only if the custom-domain attach failed half-way; the
// synchronous attach in _create_tenant flips it straight to "active"
// (or "failed" when Cognito/Route53 errors out — operator can re-PUT).
export type CognitoDomainStatus = "none" | "active" | "failed";

// Where a federated user's roles come from (spec §2.4). ``invitation``: the
// directory only authenticates, roles are granted in the tenant's console.
// ``directory``: the directory sends ``<stage>.<role>`` values, the account is
// created at the first sign-in.
export type IdpRoleSource = "invitation" | "directory";

// A company directory plugged into the tenant's pool (SAML only). ``id`` is the
// provider name in Cognito and never changes once created.
export interface IdentityProvider {
  id: string;
  displayName: string;
  protocol: "saml";
  // Exactly one of the two: a URL (Entra) or the metadata document itself
  // (Google Workspace publishes no URL). The other comes back ``null``.
  metadataUrl?: string | null;
  metadataFile?: string | null;
  emailDomains: string[];
  roleSource: IdpRoleSource;
  // ``roles`` comes back ``null`` on the wire when absent, not missing.
  claims: { email: string; roles?: string | null };
  // Nearest ``notAfter`` of the directory's signing certificates (epoch ms),
  // computed by the server on every save and heal; ``null`` = unknown (a
  // metadata URL it could not fetch or read).
  signingCertificateExpiresAt: number | null;
}

export interface TenantMetadata {
  tenantId: string;
  displayName: string;
  domain: string;
  ownPartnershipId: string;
  // Admin contact — required on create. Bound into ``stage:<stage>:admin``
  // on every stage creation; the deploy webhook re-runs the bind as a safety
  // net on every successful deploy.
  adminEmail: string;
  adminGivenName: string;
  adminFamilyName: string;
  // Per-tenant authentication. The raw pool id/arn are kept server-side and
  // never sent to the GUI; the API surfaces ``authProvisioned`` (the pool
  // exists) plus the public login domain. ``cognitoCustomDomainUrl`` is the
  // public sign-in URL (not a secret); ``cognitoDomainStatus`` is a plain
  // enum used to gate the "retry" action.
  authProvisioned: boolean;
  cognitoCustomDomainUrl: string | null;
  cognitoDomainStatus: CognitoDomainStatus;
  debug: boolean;
  logRetentionDays: number;
  mediaRetentionDays: number;
  audioRawRetentionDays: number | null;
  monitoringUserName: string | null;
  // Per-tenant gate for the dataplane's cost-estimation GUI card. The estimator
  // Lambda + the /supervision/costs API are always deployed; this only toggles
  // the SPA surface. Takes effect on the tenant's next deploy.
  costEstimationUiEnabled: boolean;
  // Second factor required for every user of this tenant. Tenant-wide, not per
  // stage: one Cognito pool serves all the tenant's stages, so turning this on
  // applies to staging and production at once. Enforced by Cognito itself —
  // there is no application-side check to bypass.
  mfaRequired: boolean;
  // Company directories of the tenant (tenant-wide: one pool serves every
  // stage). Offered at sign-in per stage — not before the account-linking lot.
  identityProviders: IdentityProvider[];
  // How users sign in: ``mixed`` = password or company directory, ``exclusive``
  // = company directory only (the emergency accounts keep a password).
  ssoMode: SsoMode;
  // The accounts designated as emergency access (no status here: the live
  // status comes from ``GET /tenants/{id}/emergency-accounts``).
  emergencyAccounts: { username: string; email: string; addedAt: string }[];
  // What the customer's IT enters in its SAML application: derived from the
  // pool (Entity ID) and the login domain (Reply URL). Null while the pool or
  // the domain is not there.
  samlEntityId: string | null;
  samlReplyUrl: string | null;
  // enabledOptions / allowedGatewayTypes / allowedCustomerInstances moved to
  // TenantStage (per-stage scoping).
  branding: Record<string, unknown> | null;
  status: TenantStatus;
  createdAt: number;
}

// Per-stage continuous-deployment policy (mirrors the backend
// ``AutoDeployPolicy`` Literal). ``none`` = manual deploys only; ``latest`` =
// auto-deploy the newest build of any channel (feature-branch beta included),
// non-prod only; ``latest_prod_only`` = auto-deploy only the newest
// production-channel build (default-branch commits). The value stays
// snake_case on the wire — convert_keys_to_camel only rewrites keys.
export type AutoDeployPolicy = "none" | "latest" | "latest_prod_only";

export interface TenantStage {
  stage: string;
  // Derived business state (the raw account id / role ARN / client id / VPC
  // CIDR that back these are kept server-side and never sent to the GUI):
  //  - ``awaitingClaim``: the environment hasn't been claimed yet (no AWS
  //    account linked) — show the Launch URL.
  //  - ``environmentClaimed``: the tenant AWS account is linked.
  //  - ``loginReady``: the sign-in client is provisioned.
  //  - ``publicUrl``: the stage's public URL (computed server-side).
  awaitingClaim: boolean;
  environmentClaimed: boolean;
  loginReady: boolean;
  publicUrl: string;
  awsRegion: string;
  urlPrefix: string | null;
  isProduction: boolean;
  // Set to true by the deploy_webhook on a successful destroy terminal
  // callback. The stage survives so the deployment timeline + cleanup
  // remain inspectable; DELETE /stages/{stage} is the next step.
  isDestroyed: boolean | null;
  // Operator-pinned customer-gateway Docker build_id. null = follow latest
  // production build (catalog_read resolves via the central SSM pointer).
  // Set to a concrete ``build-YYYYMMDD-N`` to freeze the version (e.g. for
  // a canary stage or rollback). Surfaced by the M2M for-tenant catalog
  // response as ``resolvedCustomerGwBuildId``.
  pinnedCustomerGwBuildId: string | null;
  // Per-stage authorization to run beta-channel customer-gateway builds
  // (gw-docker branches other than main). While false, put_stage refuses a
  // beta pin and the dataplane hides/refuses beta builds. Default false.
  allowBetaGatewayBuilds: boolean;
  // Per-stage automatic customer-gateway updates: while true the dataplane's
  // 15-minute drift sweep updates drifted gateways toward the stage target
  // without operator action. Default false.
  autoUpdateCustomerGateways: boolean;
  // GUI-only toggle set (never read at runtime). Per-stage since the
  // per-stage refactor.
  enabledOptions: TenantOption[];
  // Catalog allowlists keyed by model slug: ``{ "<model>": ["<version>", …] }``
  // where each version is either ``"*"`` (any version of the model) or a
  // semver ``vX.Y.Z``. Null = no instance of that kind allowed (the catalog
  // read returns nothing for that kind). Drive the M2M catalog filtering.
  allowedGatewayTypes: Record<string, string[]> | null;
  allowedCustomerInstances: Record<string, string[]> | null;
  // Per-stage gate for the dataplane Diagnostics GUI section (SCTE timing tool
  // & future diagnostic pages). GUI-only; the diagnostic route is always
  // deployed. Forwarded to the SPA as VITE_DIAGNOSTICS_UI_ENABLED on the next
  // deploy. Per-stage so staging can run diagnostics while production stays clean.
  diagnosticsUiEnabled: boolean;
  // Per-stage gate for the dataplane in-GUI bug tracker. Forwarded to the SPA
  // as VITE_BUG_TRACKER_ENABLED on the next deploy, and enforced at ingestion
  // by the bug_report_ingester Lambda (defense in depth).
  bugTrackerEnabled: boolean;
  // Per-stage gate for the companion apps screens of the dataplane GUI.
  // GUI-only: pairing keeps working server-side either way. Forwarded to the
  // SPA as VITE_COMPANION_APPS_ENABLED on the next deploy.
  companionAppsEnabled: boolean;
  // Per-stage gate for the dataplane GrandCentral MCP server. Unlike the other
  // GUI gates this one is read at runtime: the materializer turns it into
  // ``"MCP"`` membership in enabled_options, which gates the MCP stack
  // deployment on the next deploy.
  mcpEnabled: boolean;
  // Audience Measurement option (stage switch), how long the facts keep full
  // IP addresses (null = 90 days), and the measurement presets activated.
  audienceMeasurementEnabled: boolean;
  audienceMeasurementIpRetentionDays: number | null;
  measurementPresets: string[];
  // The stage's SSO App Client (``hapsout-sso-<tenant>-<stage>``): present as
  // soon as the stage enables a directory. A public client id, not a secret.
  ssoClientId?: string | null;
  // Ids of the tenant's identity providers (metadata.identityProviders)
  // offered on this stage's sign-in page. Empty = no directory offered here,
  // even if the tenant declares some. Backend rejects an id the tenant does
  // not declare.
  enabledIdentityProviders: string[];
  // Per-stage raw-PCM S3 retention (days). Drives the dataplane raw audio
  // bucket lifecycle (= DVR / catch-up window) on the next deploy. null =
  // inherit the tenant default.
  mediaRetentionDays: number | null;
  // Per-stage audience "live" threshold (ms): how far behind the live edge a
  // listener can be and still count as live. Applied on the next deploy.
  // null = inherit the tenant default (audience_config), then the dataplane's 30 000.
  audienceLiveThresholdMs: number | null;
  // Continuous-deployment policy. ``none`` = manual deploys only. When set, the
  // control-plane auto_deploy_reconciler redeploys this stage toward its policy
  // target build whenever a build is registered (and immediately on enable).
  // Production stages are restricted to ``none`` / ``latest_prod_only``.
  autoDeploy: AutoDeployPolicy;
  // Licence technique — le levier commercial. ``suspended`` ne coupe rien
  // immédiatement : le control plane cesse d'émettre et la licence en place
  // court jusqu'à son terme (trente jours au plus). Le backend le projette
  // depuis toujours ; seul l'écrivain manquait.
  licenseStatus: "active" | "suspended";
  // Ce que la sonde publique du stage a résolu, relevé une fois par jour par
  // license_push. À CONFRONTER à l'échéance émise : une licence émise que
  // personne ne lit paraîtrait saine sans cette colonne.
  probeState?: "valid" | "expired" | "invalid" | "unreachable" | null;
  probeExpiresAt?: number | null; // unix secondes
  probeAt?: number | null; // ms epoch
  probeDetail?: string | null;
  // L'autre moitié : l'échéance que le bucket porte vraiment, relevée au même
  // passage que le probe. Les confronter n'a de sens que datées ensemble.
  issuedExpiresAt?: number | null; // unix secondes
  // AWS regions a customer gateway on this stage may ingest from (data-path
  // regionalization). The dataplane builds a regional encoder stack + bucket per
  // region and validates a gateway's region against this set. null/absent =
  // single-region (deploy region only).
  enabledIngestRegions?: string[] | null;
  // What is currently deployed on this stage — set by the deploy webhook on a
  // successful deploy. null until the first successful deploy after the
  // build-versioning epic shipped.
  deployedBuildId?: string | null;
  deployedVersions?: BuildComponents | null;
  deployedAt?: number | null;
}

export type SsoMode = "mixed" | "exclusive";

// ``missing`` = designated but gone from the sign-in pool.
export type EmergencyAccountStatus = "active" | "disabled" | "missing";

export interface EmergencyAccount {
  username: string;
  email: string;
  addedAt: string;
  status: EmergencyAccountStatus;
}

export interface ListEmergencyAccountsResponse {
  items: EmergencyAccount[];
}

export interface TenantDetail {
  metadata: TenantMetadata;
  stages: TenantStage[];
}

export type DeploymentEventStatus =
  | "started"
  | "succeeded"
  | "failed"
  | "skipped";

export interface DeploymentEvent {
  step: string;
  status: DeploymentEventStatus;
  startedAt: number;
  durationMs: number | null;
  message: string | null;
}

export interface Deployment {
  runId: string;
  stage: string;
  kind: DeploymentKind;
  status: DeploymentStatus;
  triggeredAt: number;
  completedAt: number | null;
  ghaRunUrl: string | null;
  ghaRunId: string | null;
  conclusion: string | null;
  stackOutputs: Record<string, unknown> | null;
  durations: Record<string, unknown> | null;
  errorMessage: string | null;
  // Dataplane build selected for this deploy (12-hex). null on a legacy
  // latest-master deploy.
  buildId?: string | null;
  // Component versions actually deployed (reported at deploy end).
  deployedVersions?: BuildComponents | null;
  // Per-step progress emitted by deploy-tenant.yml. Empty for old runs.
  events: DeploymentEvent[];
}

export interface ListDeploymentsResponse {
  items: Deployment[];
}

// Row of the global task feed (GET /deployments — all tenants at once). Same
// shape as a per-tenant Deployment plus the owning ``tenantId`` so the bottom
// task panel can label each row.
export interface RecentDeployment extends Deployment {
  tenantId: string;
}

export interface ListRecentDeploymentsResponse {
  items: RecentDeployment[];
}

export interface ListTenantsResponse {
  items: TenantMetadata[];
  nextCursor: Record<string, unknown> | null;
}

export type GatewayCatalogKind = "cloud" | "customer" | "section";
// Subset of GatewayCatalogKind that corresponds to versioned instance
// entries — sections are excluded because they're not versioned and use a
// distinct REST subtree.
export type GatewayInstanceKind = "cloud" | "customer";

export interface GatewayCatalogItem {
  kind: GatewayCatalogKind;
  // Slug of the gateway model — sort-key fragment in DDB. Replaces the
  // pre-versioning ``name`` field (renamed in catalog Phases A+B).
  model: string;
  // ``vX.Y.Z`` for instance entries (kind = "cloud" | "customer"), null for
  // sections (which are not versioned).
  version: string | null;
  // A customer instance carries EITHER a recipe (composed from catalog
  // components — the only form the GUI writes) OR, for an entry seeded
  // before the components migration and never re-saved since, a legacy
  // monolithic payload. cloud and section entries always carry a payload
  // and never a recipe. See GatewayCatalogItem in
  // lambda-layers/control_plane_layer/python/models.py.
  payload: Record<string, unknown> | null;
  recipe: GatewayRecipe | null;
  updatedAt: number;
}

export interface ListCatalogResponse {
  items: GatewayCatalogItem[];
}

// ---------------------------------------------------------------------------
// Gateway catalog components — reusable building blocks composing customer
// gateway types (CATALOG#gw-components). Mirrors GatewayComponent /
// RecipeComponentRef / GatewayRecipe / ComponentUsage in
// lambda-layers/control_plane_layer/python/models.py and the
// lambdas/catalog_admin/handler.py routes under /catalog/gateways/components.
// ---------------------------------------------------------------------------

/**
 * Canonical group order — mirrors ``COMPONENT_GROUPS`` in
 * lambda-layers/control_plane_layer/python/models.py, itself derived from
 * the ``GatewayComponentGroup`` Literal. Single source on this side too:
 * the union below is derived from this list rather than spelled out a
 * second time, and every page orders and labels its groups from here.
 * DynamoDB's natural item order is alphabetical, which puts "features"
 * before "inputs" — not the order an operator thinks in.
 */
export const COMPONENT_GROUPS = [
  "core",
  "inputs",
  "outputs",
  "widgets",
  "features",
] as const;

export type GatewayComponentGroup = (typeof COMPONENT_GROUPS)[number];

/**
 * Groups that contribute to the composed ``capabilities`` block — mirrors
 * CAPABILITY_GROUPS in models.py. ``core`` and ``features`` describe the
 * gateway's infrastructure, not something it can advertise, so the backend
 * refuses a capability_key on those.
 */
export const CAPABILITY_GROUPS = [
  "inputs",
  "outputs",
  "widgets",
] as const satisfies readonly GatewayComponentGroup[];

export interface ComponentParam {
  key: string;
  minimum: number;
  maximum: number;
  default: number;
}

/**
 * Where a component came from when it wasn't typed by hand — mirrors
 * ``ComponentSource`` in models.py. Set by the build webhook the first time
 * it imports (or re-imports) a docker-compose service; absent for every
 * hand-authored component (the entire fleet as of this writing).
 */
export interface ComponentSource {
  repo: string;
  service: string;
  buildId: string;
  importedAt: number;
}

/**
 * A change an import proposes without applying — mirrors ``PendingImport``
 * in models.py. Only ``inclusion`` and ``mandatory`` ever appear here: they
 * are the two fields that commit the whole fleet, so an import records a
 * proposal instead of writing them, and a human accepts it via
 * ``acceptPendingImport``. Never both null — the backend rejects a
 * ``PendingImport`` that proposes neither.
 */
export interface PendingImport {
  inclusion: "always" | "optional" | null;
  mandatory: boolean | null;
  proposedAt: number;
  buildId: string;
}

export interface GatewayComponent {
  group: GatewayComponentGroup;
  name: string;
  /** Docker block consumed by the dataplane — kept in snake_case. */
  body: Record<string, unknown>;
  capabilityKey: string | null;
  capability: Record<string, unknown> | null;
  inclusion: "always" | "optional";
  params: ComponentParam[];
  updatedAt: number;
  /** Where this component came from — null for every hand-authored one. */
  source: ComponentSource | null;
  /** A change an import proposed and nobody has accepted yet. */
  pendingImport: PendingImport | null;
}

export interface RecipeComponentRef {
  ref: string;
  capability: Record<string, number>;
}

export interface GatewayRecipe {
  description: string;
  osSupported: string;
  architecture: string[];
  releaseChannels: string[];
  components: RecipeComponentRef[];
  bootstrap: {
    psVirtAudioCount: number;
    psVirtAudioSubstreams: number;
  };
  displayName: string | null;
  designNote: string | null;
}

export interface ComponentUsage {
  types: { model: string; version: string }[];
  // Named `tenants`, not `stages`: the backend's
  // find_tenants_referencing_gateway only ever returns deduplicated tenant
  // ids, never stage names (see handler.py `_component_usage`). Calling this
  // `stages` would promise data the API never sends.
  tenants: { tenantId: string; model: string }[];
}

export interface CatalogHealth {
  healthy: boolean;
  anomalyCount: number;
  findings: {
    code: string;
    severity: "anomaly" | "warning";
    message: string;
    subject: string;
  }[];
}

export interface OnboardingUrlResponse {
  launchStackUrl: string;
  expiresAt: number;
}

// ---------------------------------------------------------------------------
// Dataplane build catalogue (CATALOG#builds)
// ---------------------------------------------------------------------------

export type BuildChannel = "production" | "beta";

export interface BuildComponent {
  version: string;
}

export interface BuildComponents {
  rust: BuildComponent;
  gui: BuildComponent;
  cdk: BuildComponent;
  pslib: BuildComponent;
}

export interface DataplaneBuild {
  buildId: string;
  commitSha: string;
  channel: BuildChannel;
  branch: string;
  builtAt: number;
  components: BuildComponents;
  updatedAt: number;
}

export interface ListBuildsResponse {
  items: DataplaneBuild[];
}

// -- Backlog (GitHub Project) ------------------------------------------------

export interface BacklogItem {
  /** Project item node id — the handle for Priority/Area/Status and ordering. */
  itemId: string;
  /** Issue number in its own repository, NOT unique across the backlog. */
  number: number;
  title: string;
  url: string;
  state: "open" | "closed";
  body: string;
  updatedAt: string;
  repository: string;
  labels: string[];
  priority: string | null;
  area: string | null;
  status: string | null;
}

export interface ListBacklogResponse {
  items: BacklogItem[];
  nextCursor: string | null;
}

export interface BacklogMeta {
  projectUrl: string;
  repos: string[];
  /**
   * Subset of `repos` that actually runs the development agent. Elsewhere the
   * hand-off is refused, so the button is disabled rather than offering an
   * action the server will reject.
   */
  agentRepos: string[];
  /** Label whose presence means "handed to the agent" (`ready-for-claude`). */
  agentLabel: string;
  /** Field name → selectable option names, e.g. `{ Priority: ["P0", …] }`. */
  fields: Record<string, string[]>;
}

export interface CreateBacklogItemInput {
  repo: string;
  title: string;
  body?: string;
  priority?: string;
  area?: string;
  status?: string;
}

export interface UpdateBacklogItemInput {
  priority?: string;
  area?: string;
  status?: string;
  /**
   * Three-valued, and the distinction matters: omit the key to leave the
   * ordering alone, pass `null` to move the item to the top, pass an item id
   * to place it right after that item. Sending `null` on every edit would
   * send each field change to the top of the backlog.
   */
  afterItemId?: string | null;
}

// ---------------------------------------------------------------------------
// Messaging — journal des envois (spec 2026-09-05 §4, §7)
// ---------------------------------------------------------------------------

export interface MessagingEmailEvent {
  type: string;
  at: number; // ms epoch
  detail?: Record<string, unknown> | null;
}

export interface MessagingTag {
  name: string;
  value: string;
}

export type MessagingTransport = "ses" | "resend";

export interface MessagingEmail {
  emailId: string;
  transport: MessagingTransport;
  messageId?: string | null;
  from: string;
  to: string[];
  subject: string;
  kind: string;
  tenantId?: string | null;
  stage?: string | null;
  campaignId?: string | null;
  cp: string;
  origin: "emitter" | "webhook";
  sentAt: number; // ms epoch
  lastEvent: string | null;
  lastEventAt: number | null;
  events: MessagingEmailEvent[];
  tags: MessagingTag[];
}

export interface ListMessagingEmailsResponse {
  items: MessagingEmail[];
  nextCursor: string | null;
}

export interface MessagingDeliveryEvents {
  source: "transactional" | "waitlist";
  lastReceivedAt: number | null;
  lastEventType: string | null;
  sentLast24h: number;
}

export interface MessagingStatus {
  // One entry per source of delivery events, in a list (never keyed by source).
  deliveryEvents: MessagingDeliveryEvents[];
  last24h: {
    sent: number;
    delivered: number;
    bounced: number;
    complained: number;
    truncated: boolean;
  };
}

export type MessagingTemplateCategory = "service" | "news";

export interface MessagingTemplate {
  slug: string;
  name: string;
  category: MessagingTemplateCategory;
  subject: string;
  heading: string;
  bodyMarkdown: string;
  replyTo: string;
  variables: string[];
  createdBy: string;
  updatedBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface MessagingTemplateInput {
  slug: string;
  name: string;
  category: MessagingTemplateCategory;
  subject: string;
  heading: string;
  bodyMarkdown: string;
  replyTo: string;
}

export type MessagingPreviewProfile = "tenant_user" | "tenant_admin" | "operator";

export interface MessagingPreview {
  subject: string;
  html: string;
  text: string;
}

export interface MessagingTestResult {
  emailId: string;
  to: string;
}

// ---------------------------------------------------------------------------
// Messaging — campagnes (plan B, tâche 10)
// ---------------------------------------------------------------------------

export type MessagingSourceKind = "tenant_users" | "tenant_admins" | "operators";

export interface MessagingAudienceSelector {
  kind: MessagingSourceKind;
  tenantIds?: string[] | null;
  stages?: string[] | null;
  filter?: "mfa_not_enabled" | null;
  confirmedOnly?: boolean;
}

export type MessagingCampaignStatus =
  | "draft"
  | "resolving"
  | "ready"
  | "sending"
  | "sent"
  | "failed"
  | "cancelled";

export interface MessagingCampaignCounts {
  resolved: number;
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  duplicates: number;
}

export interface MessagingCampaign {
  id: string;
  name: string;
  templateSlug: string;
  templateSnapshot?: MessagingTemplate | null;
  audience: MessagingAudienceSelector[];
  status: MessagingCampaignStatus;
  counts: MessagingCampaignCounts;
  perSource: { kind: MessagingSourceKind; count: number }[];
  warnings: string[];
  requiresProductionKey: boolean;
  resolvedAt?: number | null;
  sendStartedAt?: number | null;
  completedAt?: number | null;
  cancelRequested: boolean;
  error?: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface MessagingCampaignInput {
  name: string;
  templateSlug: string;
  audience: MessagingAudienceSelector[];
}

export interface MessagingRecipient {
  email: string;
  source: MessagingSourceKind;
  tenantId: string | null;
  stage: string | null;
  isProduction: boolean;
  status: "pending" | "sent" | "failed" | "skipped";
  emailId: string | null;
  error: string | null;
  sentAt: number | null;
}

export interface ListMessagingCampaignsResponse {
  items: MessagingCampaign[];
  nextCursor: string | null;
}

export interface ListMessagingRecipientsResponse {
  items: MessagingRecipient[];
  nextCursor: string | null;
}
